import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useCommentNoteMutation } from "../../../../../app/notesApiSlice";

const NoteComments = ({ sharedNote, emptyMessage = "No comments have been submitted yet." }) => {
  const [comment, setComment] = useState("");
  const [commentNote, { isLoading }] = useCommentNoteMutation();

  const comments = sharedNote?.comments || [];

  const getCommenterName = (item) => {
    return item?.commenter?.name || item?.user?.username || "Unknown user";
  };

  const getCommenterRole = (item) => {
    return item?.commenter?.role || "User";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSubmit = async () => {
    const value = comment.trim();
    if (!value || !sharedNote?.id) return;

    try {
      await commentNote({ id: sharedNote.id, comment: value }).unwrap();
      setComment("");
    } catch (error) {
      console.error("Failed to post note comment:", error);
    }
  };

  if (!sharedNote) {
    return (
      <Typography color="text.secondary">
        This note has not been shared yet. Share it before adding or receiving comments.
      </Typography>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Card variant="outlined" sx={{ borderRadius: "12px" }}>
        <CardContent>
          <Stack spacing={1.5}>
            <TextField
              label="Add a comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                  event.preventDefault();
                  handleSubmit();
                }
              }}
              fullWidth
              multiline
              minRows={3}
              placeholder="Write a comment about this note..."
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading || !comment.trim()}
                endIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                sx={{ textTransform: "none" }}
              >
                {isLoading ? "Posting..." : "Post Comment"}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {comments.length === 0 ? (
        <Typography color="text.secondary">{emptyMessage}</Typography>
      ) : (
        comments.map((item) => (
          <Card key={item.id} variant="outlined" sx={{ borderRadius: "12px" }}>
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={0.5} sx={{ mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {getCommenterName(item)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getCommenterRole(item)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {formatDate(item.posted_at)}
                </Typography>
              </Stack>
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {item.comment}
              </Typography>
            </CardContent>
          </Card>
        ))
      )}
    </Stack>
  );
};

export default NoteComments;
